<script setup lang="ts">
import { formatDateTime } from "@/services/functions";
import ResizableList from '@/components/ResizableList.vue';
import {
  MANAGEMENT_OFFICE,
  SUB_MANAGEMENT_OFFICE,
  INSTALLATION_OFFICE,
  OPERATION_OFFICE,
  DEPARTMENT
} from "@/services/consts/user_role";
import Api from "@/services/api/ApiServiceFabrick"
import { useAuth } from "@/services/user/Auth0UserProvider"
import 'gitart-vue-dialog/dist/style.css'
import { GDialog } from 'gitart-vue-dialog'
import { ref } from 'vue'
import getUserRolesResponseType from "@/types/responses/getUserRoles"
import i18n from '@/lang/i18n';
import EditLinkIcon from "@/components/Common/EditLinkIcon.vue"

const { loginWithRedirect } = useAuth()

const dialogState = ref(false)
const updatingStatus= ref(false)
const deleteItemId= ref(null as number | null)
const errorMessages = ref({} as {id: string[], full_name: string[]})

const props = defineProps<{
  isLoading: boolean,
  isSearchCompleted: boolean,
  deleteAuthority: boolean,
  showFlash: (message: string, type: string, fadeout: boolean) => {value: any},
  userRoles: getUserRolesResponseType[]
}>()
const emit = defineEmits<{
  (e: 'deleteUserRole', id: number): void
}>()

const HEADER = ([
  i18n.global.t('pages.userRoles.listHeader.roleName'),
  i18n.global.t('pages.userRoles.listHeader.officeableType'),
  i18n.global.t('pages.userRoles.listHeader.officeName'),
  i18n.global.t('pages.userRoles.listHeader.departmentName'),
  i18n.global.t('pages.userRoles.listHeader.updatedAt'),
  i18n.global.t('pages.userRoles.listHeader.updatedBy'),
  i18n.global.t('pages.userRoles.listHeader.form'),
  i18n.global.t('pages.userRoles.listHeader.delete'),
])

function deleteUserRole () {
  if(!deleteItemId.value) return
  updatingStatus.value = true
  Api().deleteUserRoleForm(deleteItemId.value).then((data: {status:boolean}) => {
    updatingStatus.value = false
    if(deleteItemId.value) {
      emit('deleteUserRole', deleteItemId.value)
    }
    closeModal()
    props.showFlash(i18n.global.t('pages.common.messages.removeSuccess'), "success", true)
  }).catch((error: any) => {
    updatingStatus.value = false
    return errorHandle(error.response)
  })
}

function errorHandle (res: any) {
  switch (res.status) {
    case 401:
      loginWithRedirect({ appState: { targetUrl: location.pathname } })
      break
    case 403:
      props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", true)
      break
    case 422:
      errorMessages.value = res.data.errors
  }
}

function convertOrganizationType(organization_type: string){
  let convertedType = {} as {id: number, name: string, type: string} | undefined
  const organizations = [
    MANAGEMENT_OFFICE,
    SUB_MANAGEMENT_OFFICE,
    INSTALLATION_OFFICE,
    OPERATION_OFFICE,
    DEPARTMENT
  ]

  if (organization_type == DEPARTMENT.type) {
    return OPERATION_OFFICE.name
  } else {
    convertedType = organizations.find((object) => {
      return organization_type == object.type
    })
  }
  return convertedType? convertedType.name:""
}

function openModal(id: number) {
  deleteItemId.value = id
  dialogState.value = true
  errorMessages.value = {id: [], full_name: []}
}
function closeModal() {
  deleteItemId.value = null
  dialogState.value = false
}

</script>
<template>
  <div class="user-role-list">
    <template v-if="!props.isSearchCompleted">
      <div class="not-searched">
        <label>
          {{ i18n.global.t('pages.common.list.runFilter') }}
        </label>
      </div>
    </template>
    <template v-if="props.isLoading">
      <div class="not-searched">
        <label>
          {{ i18n.global.t('pages.common.list.reading') }}
        </label>
      </div>
    </template>
    <ResizableList
      scrollAreaSelector=".v-table__wrapper"
    >
      <div class="table-scrollbar">
        <v-table>
          <thead>
            <tr class="table-header-row">
              <template v-for="(item, index) in HEADER" :key="item">
                <th>
                  <div :class="index === 0 ? 'ml-2' : ''">
                    {{ item }}
                  </div>
                </th>
              </template>
            </tr>
          </thead>
          <tbody>
            <template v-if="props.userRoles?.length != 0">
              <tr
                v-for="(item, index) in props.userRoles"
                :key="index"
              >
                <td>{{ item.display_name }}</td>
                <td>{{ convertOrganizationType(item.organization_type) }}</td>
                <td>{{ item.company_name }}</td>
                <td>{{ item.department_name }}</td>
                <td>{{ formatDateTime(new Date(item.updated_at)) }}</td>
                <td>{{ item.account?.full_name }}</td>
                <td>
                  <EditLinkIcon :name="'userRoleForm'" :id="item.id" />
                </td>
                <td>
                  <button
                    v-if="props.deleteAuthority"
                    @click="openModal(item.id)"
                    class="d-flex material-symbols-outlined"
                  >
                    delete
                  </button>
                </td>
              </tr>
            </template>
            <template v-else-if="props.isSearchCompleted && !props.isLoading">
              <tr>
                <td colspan="9">
                  {{ i18n.global.t('pages.common.list.noData') }}
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </div>
    </ResizableList>
  </div>

  <GDialog
    v-model="dialogState"
    max-width="20%"
    transition="custom-from-bottom-transition"
  >
    <div class="modal-header justify-content-center pt-3">
      <label>{{ i18n.global.t('common.confirmationModal.title') }}</label>
    </div>

    <div class="row mr-3 ml-3 pl-5 v-theme--light">
      {{ i18n.global.t('pages.userRoles.modal.message') }}
    </div>

    <template v-for="(item) in errorMessages.id" :key="item">
      <div class="ml-3 pl-5 text-error">{{ item }}</div>
    </template>
    <template v-for="(item) in errorMessages.full_name" :key="item">
      <div class="ml-3 pl-8 text-error">{{ item }}</div>
    </template>

    <div class="d-flex justify-content-end m-3">
      <v-btn
        class="m-1 reset-btn"
        @click="closeModal"
        :disabled="updatingStatus"
      >
      {{ i18n.global.t('pages.userRoles.modal.cancelBtn') }}
      </v-btn>

      <v-btn
        class="m-1 exec-btn"
        @click="deleteUserRole()"
        :loading="updatingStatus"
      >
      {{ i18n.global.t('pages.userRoles.modal.submitBtn') }}
      </v-btn>
    </div>
  </GDialog>

</template>
<style>
.user-role-list .v-table__wrapper {
  height: var(--resizable-height, var(--list-default-height));
  overflow: auto;
}
</style>
<style scoped>
.user-role-list {
  position: relative;
}
.user-role-list table th {
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  min-width: 7em;
}
.checkbox-in-grid {
  padding: 1vm;
  height: inherit !important;
  display: inline-grid !important;
}
.list-loading {
  position: absolute;
  min-width: 58vw;
  height: 100%;
  overflow: auto;
  z-index: 1000;
  background-color: rgba(80,80,80,0.5);
  right: 0;
  left: 0;
}
.list-loading>label {
  font-weight: bold;
  color: white;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateY(-50%) translateX(-50%);
}
.column-space {
  white-space: nowrap;
}
</style>
